import { useAuth } from "@clerk/tanstack-react-start";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { READINESS_PUBLIC_PATH } from "~/lib/shared/readiness/routes";

const MAGIC_LINK_REASONS = [
  "magic-link-device-mismatch",
  "magic-link-expired",
  "magic-link-already-used",
  "magic-link-failed",
] as const;

type MagicLinkReason = (typeof MAGIC_LINK_REASONS)[number];

const STATUS_TO_REASON: Record<string, MagicLinkReason> = {
  client_mismatch: "magic-link-device-mismatch",
  expired: "magic-link-expired",
  verified_on_other_client: "magic-link-already-used",
};
/**
 * The readiness results page sends her through a magic link to start the
 * trial and expects her back on the page, with `next=trial`, to continue.
 */
const READINESS_RETURN_PARAM = "next";
const READINESS_RETURN_VALUE = "trial";

/**
 * Intercepts Clerk `__clerk_status` query params after magic-link redirects.
 *
 * - Error statuses (client_mismatch, expired, verified_on_other_client)
 *   redirect to /login with an error banner.
 * - The "verified" success status waits for the session to be established,
 *   then redirects to /dashboard so the tab that opened the magic link
 *   also lands somewhere useful.
 */
export function ClerkStatusHandler(): null {
  const { isSignedIn, isLoaded } = useAuth();
  const navigate = useNavigate();
  const pendingVerified = useRef(false);
  const pendingVerifiedPath = useRef<string | null>(null);

  useEffect(() => {
    if (!isLoaded) return;

    const params = new URLSearchParams(window.location.search);
    const clerkStatus = params.get("__clerk_status");
    const isReadinessReturn =
      window.location.pathname === READINESS_PUBLIC_PATH &&
      params.get(READINESS_RETURN_PARAM) === READINESS_RETURN_VALUE;
    const from = clerkStatus ? STATUS_TO_REASON[clerkStatus] : undefined;

    if (!clerkStatus && !pendingVerified.current) return;

    if (clerkStatus) {
      // Strip Clerk params from URL to prevent re-triggering
      params.delete("__clerk_status");
      params.delete("__clerk_created_session");
      if (isReadinessReturn && clerkStatus !== "verified") {
        params.delete(READINESS_RETURN_PARAM);
      }
      const cleanSearch = params.toString();
      const cleanUrl =
        window.location.pathname +
        (cleanSearch ? `?${cleanSearch}` : "") +
        window.location.hash;
      history.replaceState(null, "", cleanUrl);
    }

    // Success: magic link verified — redirect once the session is ready
    if (clerkStatus === "verified") {
      pendingVerified.current = true;
      pendingVerifiedPath.current = isReadinessReturn
        ? READINESS_PUBLIC_PATH
        : null;
    }

    if (pendingVerified.current) {
      if (isSignedIn) {
        const shouldStayOnReadiness =
          pendingVerifiedPath.current === READINESS_PUBLIC_PATH;
        pendingVerified.current = false;
        pendingVerifiedPath.current = null;
        if (!shouldStayOnReadiness) {
          navigate({ to: "/dashboard" });
        }
      } else {
        pendingVerified.current = true;
      }
      return;
    }

    // Error: redirect to login with the appropriate banner
    if (!from || isSignedIn) return;

    navigate({
      to: "/login",
      search: { from, redirect: window.location.pathname },
    });
  }, [isLoaded, isSignedIn, navigate]);

  return null;
}
